import React, { useMemo, useState } from 'react';
import { Bug, ChevronDown, ChevronRight, Copy, Terminal } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { DetrixNetworkEntry, DetrixSession } from '../../types';
import { formatDuration } from '../../utils/time';
import { generateCurl } from '../../utils/curl';
import { buildNetworkBugReportMarkdown } from '../../utils/networkBugReport';
import { prettifyJsonString } from '../../utils/prettifyJson';
import { ErrorNarrative } from '../../components/ErrorNarrative';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type SubTab = 'headers' | 'request' | 'response' | 'timing';

interface NetworkRequestInspectorProps {
  session: DetrixSession;
  entry: DetrixNetworkEntry;
}

export const NetworkRequestInspector: React.FC<NetworkRequestInspectorProps> = ({ session, entry }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('headers');
  const [sections, setSections] = useState({
    general: true,
    requestHeaders: true,
    responseHeaders: true,
  });

  const toggleSection = (section: keyof typeof sections) => {
    setSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleCopyCurl = () => {
    const curl = generateCurl(entry);
    void navigator.clipboard.writeText(curl);
  };

  const handleCopyUrl = () => {
    void navigator.clipboard.writeText(entry.request.url);
  };

  const handleCopyBugReport = () => {
    void navigator.clipboard.writeText(buildNetworkBugReportMarkdown(session, entry));
  };

  const status = entry.response?.status ?? 0;
  const statusColor =
    status >= 200 && status < 300
      ? 'text-[var(--ok)]'
      : status >= 300 && status < 400
        ? 'text-[var(--warn)]'
        : 'text-[var(--error)]';

  const failed = status >= 400 || Boolean(entry.error);
  const failureMsg = entry.error?.message ?? entry.response?.statusText ?? (status ? `HTTP ${status}` : 'Request failed');

  const requestBodyDisplay = useMemo(() => prettifyJsonString(entry.request.body), [entry.request.body]);
  const responseBodyDisplay = useMemo(() => prettifyJsonString(entry.response?.body ?? null), [entry.response?.body]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--bg-panel)]">
      {/* Narrative stays outside the scroll region so narrow widths / WebKit sticky+overflow bugs cannot hide it. */}
      <div className="shrink-0 bg-[var(--bg-panel)]">
        <ErrorNarrative session={session} />
        {failed ? (
          <div className="border-b border-[var(--inspector-error-border)] bg-[var(--inspector-error-bg)] px-3 py-2.5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--inspector-error-title)]">Request failed</div>
            <p className="mt-1 text-[12px] leading-snug text-[var(--inspector-error-body)]">{failureMsg}</p>
          </div>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
        <div className="space-y-2 border-b p-3">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0 w-full flex-1 sm:w-auto sm:min-w-[8rem]">
              <div className="mb-1 flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase text-[var(--text-secondary)]">
                  {entry.request.method}
                </span>
                <span className="truncate text-[13px] font-semibold text-[var(--text-primary)]" title={entry.request.url}>
                  {entry.request.url}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <span className={cn('font-bold', statusColor)}>
                  {status || 'No response'} {entry.response?.statusText ?? ''}
                </span>
                <span className="tabular-nums text-[var(--text-muted)]">{formatDuration(entry.timing.durationMs)}</span>
              </div>
            </div>
            <div className="flex w-full shrink-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {failed ? (
                <button
                  type="button"
                  onClick={handleCopyBugReport}
                  title="Copy markdown bug report (redacted headers, truncated bodies) for this request"
                  className="flex items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--border)]"
                >
                  <Bug className="h-3 w-3" aria-hidden />
                  <span>Copy bug report</span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleCopyCurl}
                className="flex items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--border)]"
              >
                <Terminal className="h-3 w-3" />
                <span>cURL</span>
              </button>
              <button
                type="button"
                onClick={handleCopyUrl}
                className="flex items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1 text-[11px] text-[var(--text-primary)] transition-colors hover:bg-[var(--border)]"
              >
                <Copy className="h-3 w-3" />
                <span>URL</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex h-8 shrink-0 border-b bg-[var(--bg-sidebar)] px-2" role="tablist">
          {(['headers', 'request', 'response', 'timing'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeSubTab === tab}
              onClick={() => setActiveSubTab(tab)}
              className={cn(
                'relative px-3 text-[11px] capitalize transition-colors',
                activeSubTab === tab ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
              )}
            >
              {tab}
              {activeSubTab === tab ? (
                <div className="absolute bottom-0 left-0 h-[1px] w-full bg-[var(--accent)]" />
              ) : null}
            </button>
          ))}
        </div>

        <div className="min-w-0">
        {activeSubTab === 'headers' ? (
          <div className="p-0">
            <CollapsibleSection
              title="General"
              isOpen={sections.general}
              onToggle={() => toggleSection('general')}
            >
              <div className="space-y-1 py-1">
                <KVRow label="Request URL" value={entry.request.url} valueClass="break-all text-[var(--link)]" />
                <KVRow label="Request Method" value={entry.request.method} />
                <KVRow
                  label="Status Code"
                  value={`${status || 'No response'} ${entry.response?.statusText ?? ''}`}
                  valueClass={statusColor}
                />
                {entry.error ? <KVRow label="Error" value={entry.error.message} valueClass="text-[var(--error)]" /> : null}
              </div>
            </CollapsibleSection>

            {entry.response ? (
              <CollapsibleSection
                title="Response Headers"
                isOpen={sections.responseHeaders}
                onToggle={() => toggleSection('responseHeaders')}
              >
                <div className="space-y-1 py-1">
                  {Object.entries(entry.response.headers)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([k, v]) => (
                      <KVRow key={k} label={k} value={v} />
                    ))}
                </div>
              </CollapsibleSection>
            ) : null}

            <CollapsibleSection
              title="Request Headers"
              isOpen={sections.requestHeaders}
              onToggle={() => toggleSection('requestHeaders')}
            >
              <div className="space-y-1 py-1">
                {Object.entries(entry.request.headers)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([k, v]) => (
                    <KVRow key={k} label={k} value={v} />
                  ))}
              </div>
            </CollapsibleSection>
          </div>
        ) : null}

        {activeSubTab === 'request' ? (
          <div className="p-3">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-[11px] font-bold uppercase text-[var(--text-muted)]">Request Body</h4>
              <span className="rounded border bg-[var(--bg-elevated)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">
                {entry.request.bodyEncoding}
              </span>
            </div>
            {entry.request.body ? (
              <div className="overflow-hidden rounded border border-[var(--border)] bg-[var(--bg-editor)] p-2 font-mono text-[11px] text-[var(--code-json-fg)]">
                <pre className="whitespace-pre-wrap break-words">{requestBodyDisplay}</pre>
              </div>
            ) : (
              <div className="text-[11px] italic text-[var(--text-muted)]">No request body</div>
            )}
          </div>
        ) : null}

        {activeSubTab === 'response' ? (
          <div className="p-3">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-[11px] font-bold uppercase text-[var(--text-muted)]">Response Body</h4>
              {entry.response ? (
                <span className="rounded border bg-[var(--bg-elevated)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">
                  {entry.response.bodyEncoding}
                </span>
              ) : null}
            </div>
            {entry.response?.body ? (
              <div className="overflow-hidden rounded border border-[var(--border)] bg-[var(--bg-editor)] p-2 font-mono text-[11px] text-[var(--code-json-fg)]">
                <pre className="whitespace-pre-wrap break-words">{responseBodyDisplay}</pre>
              </div>
            ) : (
              <div className="text-[11px] italic text-[var(--text-muted)]">
                {entry.response ? 'No response body' : entry.error?.message ?? 'No response captured'}
              </div>
            )}
          </div>
        ) : null}

        {activeSubTab === 'timing' ? (
          <div className="p-3">
            <h4 className="mb-3 text-[11px] font-bold uppercase text-[var(--text-muted)]">Timing</h4>
            <div className="space-y-2">
              <KVRow label="Start (ms)" value={String(entry.timing.startMs)} />
              <KVRow label="End (ms)" value={String(entry.timing.endMs)} />
              <KVRow
                label="Duration"
                value={`${entry.timing.durationMs} ms`}
                valueClass="font-bold text-[var(--text-primary)]"
              />
            </div>
          </div>
        ) : null}
        </div>
      </div>
    </div>
  );
};

const CollapsibleSection: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, isOpen, onToggle, children }) => (
  <div className="border-b border-[var(--border-subtle)]">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-1.5 transition-colors hover:bg-[var(--bg-elevated)]"
    >
      {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      <span className="text-[11px] font-bold uppercase text-[var(--text-muted)]">{title}</span>
    </button>
    {isOpen ? <div className="px-3 pb-2">{children}</div> : null}
  </div>
);

const KVRow: React.FC<{ label: string; value: string; valueClass?: string }> = ({ label, value, valueClass }) => (
  <div className="flex gap-2 font-mono text-[11px] leading-relaxed">
    <span className="w-[120px] shrink-0 select-none text-[var(--text-secondary)]">{label}:</span>
    <span className={cn('break-all text-[var(--text-primary)]', valueClass)}>{value}</span>
  </div>
);
